'use client'

// The food you can actually see, per spot, keyed off `entity.state.supply`.
// As he eats, items disappear.
//
// This used to be primitive blocks: a red cube with a yellow lid standing
// in for a happy meal, a tan cylinder for a coffee. They were honest
// placeholders and the file said to replace them when models landed.
// food.glb is that model.
//
// ONE glb, cloned per slot, rather than a file per item: the props share
// texture atlases, so splitting them would multiply texture memory by the
// number of items and add thirty more requests. Items are recentred at
// build time (scripts/prep-food.mjs) so each one stands on its own
// origin: place it and its feet are on the floor, no nudge table to keep
// in sync with the artwork.
//
// A junk spot serves MEALS rather than loose objects: a tray with a liner
// and things arranged on it, or a happy meal, because that is what is
// actually on a counter and a floating burger beside a floating cup never
// looked like lunch. Everywhere else, single items.

import { memo, useMemo } from 'react'
// toNode is shared; the traverse stays local because it runs inside a
// useMemo ON PURPOSE: this set must convert before first paint, and the
// hook's useEffect converts after it.
import { toNode } from '../hooks/useSceneNodeMaterials'
import * as THREE from 'three/webgpu'
import useGameStore from '../../../stores/gameStore'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useModel } from '../hooks/useModel'

const MODEL = '/models/food.glb'

// A serving, as objects. Each entry is a list of parts, and each part is
// [name, x, y, z] in MODEL units before the quality's scale is applied.
//
// The tray is 2.11 x 0.11 x 2.76 and its top face is therefore y = 0.11,
// which is where everything on it sits. The liner goes down first (it is
// a flat plane, zero height) and the food on top of that.
const TRAY_TOP = 0.115
const SERVINGS = {
  junk: [
    // The full tray: liner, burger, fries, drink. The arrangement is
    // fixed rather than scattered, because a tray is a thing somebody
    // laid out, not a thing that fell.
    [
      ['Tray_Plastic', 0, 0, 0],
      ['Tray_Liner', 0, TRAY_TOP, 0],
      ['Burger_1', -0.42, TRAY_TOP, -0.55],
      ['Fries_Red', 0.52, TRAY_TOP, -0.6],
      ['Soda_Cup_Red', 0.45, TRAY_TOP, 0.75],
    ],
    [
      ['Tray_Plastic', 0, 0, 0],
      ['Tray_Liner', 0, TRAY_TOP, 0],
      ['Burger_2', -0.4, TRAY_TOP, -0.5],
      ['Fries_Blue', 0.5, TRAY_TOP, -0.6],
      ['Soda_Cup_Blue', 0.45, TRAY_TOP, 0.75],
    ],
    // The happy meal stands on its own: it is already a box with a meal
    // in it, and putting a box on a tray reads as a box on a tray.
    [['happyMeal', 0, 0, 0]],
    [['Nuggets', 0, 0, 0], ['Soda_Cup_Red', 0.75, 0, 0.35]],
    [['Milkshake', 0, 0, 0]],
  ],
  // Prawn crackers and char siu bao have no models of their own yet, so
  // the takeaway box stands in for both, which is honest enough: it is
  // what the food arrives in either way. Sam's call, revisit if they get
  // modelled.
  chinese: [
    [['Noodles', 0, 0, 0]],
    [['Bowl_Soup', 0, 0, 0]],
    [['Noodles', 0, 0, 0], ['Bowl_Soup', 0.9, 0, 0.5]],
  ],
  caffeine: [
    [['coffeeCup', 0, 0, 0]],
    [['coffeeCup', 0, 0, 0], ['Donut_1', 0.75, 0, 0.4]],
    [['Donut_2', 0, 0, 0]],
    [['Donut_3', 0, 0, 0], ['Cinnamon_bun', 0.8, 0, 0.3]],
  ],
  healthy: [[['Apple', 0, 0, 0]]],
  // The bakery. Bread on the counter, one or two things at a time.
  bakery: [
    [['Bread_1', 0, 0, 0]],
    [['Bread_2', 0, 0, 0]],
    [['Bread_roll', 0, 0, 0], ['Bread_roll', 0.55, 0, 0.35]],
    [['Sandwich_sub', 0, 0, 0]],
    [['Chocolate Brioche_Chocolate Brioche_0', 0, 0, 0]],
    [['Cinnamon_bun', 0, 0, 0], ['Bread_roll', 0.6, 0, 0.4]],
  ],
  // The soup cart he asked for: a bowl, or a bowl with a roll beside it.
  soup: [
    [['Bowl_Soup', 0, 0, 0]],
    [['Bowl_Soup', 0, 0, 0], ['Bread_roll', 0.75, 0, 0.35]],
  ],
  pizza: [
    [['Pizza_1', 0, 0, 0]],
    [['Pizza_2', 0, 0, 0]],
    [['Pizza_Slice', 0, 0, 0]],
    [['Pizza_Slice_2', 0, 0, 0]],
  ],
  // The japanese restaurant on the street south of the pizza place (7 Oct):
  // a bowl of ramen, sushi on its board, a bento, gyoza, an onigiri or two.
  // Modelled for it in Kiwi_world.blend's Food collection, flat colours.
  japanese: [
    [['Ramen_Bowl', 0, 0, 0]],
    [['Sushi_Board', 0, 0, 0]],
    [['Bento_Box', 0, 0, 0]],
    [['Gyoza_Plate', 0, 0, 0]],
    [['Onigiri', 0, 0, 0], ['Onigiri', 0.8, 0, 0.3]],
  ],
  // The taco truck: plates of tacos off the spit, from the truck's own
  // PSX set, and a pair of folded ones with a wedge of lime.
  taco: [
    [['Taco_Plate_1', 0, 0, 0]],
    [['Taco_Plate_2', 0, 0, 0]],
    [['Taco_Plate_3', 0, 0, 0]],
    [['Taco_Pair', 0, 0, 0]],
  ],
  // The bar is an ACTIVITY, not a FOOD_SPOT, so it has no supply to read
  // and gets a fixed row of glasses instead.
  bar: [
    [['Beer', 0, 0, 0]],
    [['Wine', 0, 0, 0]],
    [['Whiskey', 0, 0, 0]],
    [['Whiskey2', 0, 0, 0]],
    [['Gin', 0, 0, 0]],
    [['Sake', 0, 0, 0]],
  ],
}

// Model units to world metres, per quality.
//
// Calibrated against the primitive placeholders these replace, which were
// the original author's read on how big food should look next to him: the
// junk box was 0.45 across, the apple 0.44, the pint about 0.6 tall. The
// bottles are authored much larger than everything else, hence the split.
const SCALE = {
  junk: 0.5,
  chinese: 0.5,
  caffeine: 0.65,
  healthy: 0.7,
  bakery: 0.5,
  pizza: 0.32,
  soup: 0.5,
  japanese: 0.5,
  taco: 0.5,
  bar: 0.36,
}

// How much room one serving needs, centre to centre, in world metres.
// A McDonald's tray is 2.11 x 2.76 model units at 0.5 scale, so a bit
// over a metre across its diagonal; an apple needs almost nothing.
const FOOTPRINT = {
  junk: 1.7,
  chinese: 1.0,
  caffeine: 0.9,
  healthy: 0.6,
  bakery: 1.0,
  pizza: 0.9,
  soup: 1.0,
  japanese: 1.0,
  taco: 0.8,
  bar: 0.6,
}

// Deterministic pseudo-random for consistent scatter per item slot.
function rand(seed) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

/**
 * The source scene, with its materials converted once.
 *
 * useNodeMaterials idiom, same as the buildings and the piano: a fresh
 * MeshStandardNodeMaterial with fields set across explicitly, never
 * `copy()`. This is what puts the food through the dither renderer with
 * everything else; a plain MeshStandardMaterial would light differently
 * and skip the pass entirely.
 *
 * Normal and roughness maps are not transferred because they no longer
 * exist: prep-food.mjs strips them at build time, since nothing here
 * reads them and they were 44% of the file.
 */
function usePreparedFood() {
  const { scene } = useModel(MODEL)
  return useMemo(() => {
    const converted = new Map()
    scene.traverse((o) => {
      // The export carries a stray 2-vertex LINES primitive, which three
      // loads as LineSegments and draws as a white stroke across the
      // prop. Modelling debris, same as the piano's loose edges. Hidden
      // rather than deleted so it is one line to undo.
      if (o.isLine || o.isLineSegments) { o.visible = false; return }
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      // Three meshes in this set carry more than one primitive, which can
      // reach the renderer as an ARRAY of materials. The first cut skipped
      // arrays outright, which would have quietly left those pieces on a
      // plain MeshStandardMaterial: lit differently from the rest of the
      // town and missed entirely by the dither pass. Every entry converts.
      o.material = Array.isArray(o.material)
        ? o.material.map((m) => toNode(m, converted, 0.85))
        : toNode(o.material, converted, 0.85)
    })
    const byName = new Map()
    for (const child of scene.children) byName.set(child.name, child)
    return byName
  }, [scene])
}


/**
 * One serving on the counter. `supplySlot` drives where it sits and which
 * arrangement it is, so a spot's spread is stable across re-renders.
 */
function Serving({ quality, supplySlot, index, count, byName }) {
  // Placed on a ring rather than scattered.
  //
  // Random angle and random radius put two servings on top of each other
  // often enough that a tray regularly grew a burger through its middle,
  // and the obvious fix (a wider scatter) pushed the food so far apart it
  // stopped reading as one counter. A ring sized from the serving's own
  // footprint solves both: the gap between neighbours is what it needs to
  // be and never more, so three servings sit close and five still clear
  // each other.
  //
  //   chord between neighbours = 2 R sin(pi / n)  ->  R = gap / (2 sin(pi / n))
  const n = Math.max(1, count)
  const gap = FOOTPRINT[quality] ?? 0.9
  const radius = n === 1 ? 0 : gap / (2 * Math.sin(Math.PI / n))
  // Evenly spaced, then nudged by a fraction of the step so a counter does
  // not look like a clock face. The jitter stays inside the slack the gap
  // already allows.
  const step = (Math.PI * 2) / n
  const angle = index * step + (rand(supplySlot * 12.9898) - 0.5) * step * 0.35
  const ox = Math.cos(angle) * radius
  const oz = Math.sin(angle) * radius

  const parts = useMemo(() => {
    const options = SERVINGS[quality]
    if (!options?.length) return null
    const pick = options[Math.floor(rand(supplySlot * 91.17) * options.length) % options.length]
    const built = []
    for (const [name, x, y, z] of pick) {
      const src = byName.get(name)
      // A name that is not in the glb means the artwork changed under us.
      // Skipping the part is the right answer: a gap on the tray reads as
      // a quiet day, a magenta error cube reads as a broken game.
      if (!src) continue
      const clone = src.clone(true)
      clone.position.set(x, y, z)
      built.push(clone)
    }
    return built.length ? built : null
  }, [quality, supplySlot, byName])

  if (!parts) return null
  const s = SCALE[quality] ?? 0.5
  // Turned on the spot so a row of trays does not look stamped. The whole
  // serving turns together, which is what keeps the tray a tray.
  const spin = rand(supplySlot * 22.71) * Math.PI * 2
  return (
    <group position={[ox, 0, oz]} rotation={[0, spin, 0]} scale={s}>
      {parts.map((p, i) => <primitive key={i} object={p} />)}
    </group>
  )
}

const ServingMemo = memo(Serving)

/** Group of servings for a single food spot. */
function FoodSpotItems({ entity, byName }) {
  const supply = Math.max(0, entity.state?.supply || 0)
  const quality = entity.state?.quality
  if (!quality || supply === 0) return null
  // Shut means shut: the food comes in off the counter. The sim decides
  // this (Entities.updateEntities reads the HOURS table onto the entity),
  // so the world and his own percept of it cannot disagree. `undefined`
  // means a spot with no hours at all, like the apple tree, which is
  // always there.
  if (entity.state?.open === false) return null

  const items = []
  for (let i = 0; i < supply; i++) {
    const supplySlot = hashString(entity.id) * 31 + i
    items.push(
      <ServingMemo key={i} quality={quality} supplySlot={supplySlot} index={i} count={supply} byName={byName} />,
    )
  }
  return <group position={[entity.pos.x, 0, entity.pos.z]}>{items}</group>
}

/** The bar: a fixed handful of glasses, since there is no supply to read. */
function BarDrinks({ entity, byName }) {
  const items = []
  for (let i = 0; i < 5; i++) {
    items.push(
      <ServingMemo key={i} quality="bar" supplySlot={hashString(entity.id) * 31 + i} index={i} count={5} byName={byName} />,
    )
  }
  return <group position={[entity.pos.x, 0, entity.pos.z]}>{items}</group>
}

function hashString(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h) / 1e6
}

export function FoodIndicators() {
  // Select raw entityStates (a stable reference from the store) with a custom
  // equality fn that only fires updates when something food-relevant changes.
  const entityStates = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates,
    (prev, next) => {
      const prevFood = prev.filter((e) => e.type === 'FOOD_SPOT')
      const nextFood = next.filter((e) => e.type === 'FOOD_SPOT')
      if (prevFood.length !== nextFood.length) return false
      for (let i = 0; i < prevFood.length; i++) {
        const a = prevFood[i]
        const b = nextFood[i]
        if (a.id !== b.id) return false
        if ((a.state?.supply ?? 0) !== (b.state?.supply ?? 0)) return false
        if ((a.state?.quality ?? null) !== (b.state?.quality ?? null)) return false
        // open was missing from this list, and FoodSpotItems bails on
        // open === false: a spot flipping shut compared as "equal", no
        // re-render fired, and the food stayed on the counter of a place
        // the sim had closed. The bar needs no line here: its glasses are
        // a fixed count off the entity id and read no state.
        if ((a.state?.open ?? true) !== (b.state?.open ?? true)) return false
      }
      return true
    },
  )

  const byName = usePreparedFood()

  const foodSpots = useMemo(
    () => entityStates.filter((e) => e.type === 'FOOD_SPOT'),
    [entityStates],
  )
  const bar = useMemo(
    () => entityStates.find((e) => e.type === 'ACTIVITY' && e.state?.kind === 'bar'),
    [entityStates],
  )

  return (
    <group>
      {foodSpots.map((entity) => (
        <FoodSpotItems key={entity.id} entity={entity} byName={byName} />
      ))}
      {bar && <BarDrinks entity={bar} byName={byName} />}
    </group>
  )
}

export default FoodIndicators
